// Base Map (offline Map Editor library) served by the Windows EXE from an encrypted local file
// at https://app.trmd/__mapdb.json. Falls back silently to null when not in the EXE / no local DB.
let _db = null;
const IS_WIN_EXE = typeof window !== "undefined" && window.__TRMD_NATIVE === "windows";

export async function getLocalDb() {
  if (_db) return _db;
  if (typeof window === "undefined" || !window.__TRMD_MAPDB) return null;
  try {
    const r = await fetch("https://app.trmd/__mapdb.json", { cache: "no-store" });
    _db = await r.json();
  } catch { _db = null; }
  return _db;
}

export async function loadLocalLib() {
  const d = await getLocalDb();
  if (!d || !Array.isArray(d.items)) return [];
  return d.items.map(({ bin_b64, xdf_b64, ...e }) => e);
}

export function installedBasemapVersion() {
  return typeof window !== "undefined" ? (window.__TRMD_MAPDB_VER || "") : "";
}

// Auto Sync: compares the installed Base Map version against the admin-released version.
export async function checkDbUpdate(api) {
  const installed = installedBasemapVersion();
  try {
    const { data } = await api.get("/library/version");
    const latest = data?.version || "";
    return { current: installed, latest, notes: data?.notes || "", outdated: !!latest && installed !== latest };
  } catch {
    return null;
  }
}

// Download the library with progress (0..100). part: "map" = BIN/XDF (Map Editor), "ecu" = kode motor, "all" = both.
// The chosen part is merged into the local copy; the other part is preserved so nothing is lost.
export async function downloadBasemap(api, onProgress, part = "all") {
  const installed = installedBasemapVersion();
  const local = (await getLocalDb()) || { items: [], ecu_records: [] };
  const params = { files: 0 };
  if (part !== "all") params.part = part;
  if (installed && (part === "all" || part === "map")) params.since = installed;
  // Manifest first (small), then each item's BIN/XDF separately — one huge response hits proxy size/time limits.
  const { data } = await api.get("/library/export", { params });
  const list = data.items || [];
  for (let i = 0; i < list.length; i++) {
    let last;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const r = await api.get(`/library/export/item/${list[i].id}`, { timeout: 120000 });
        list[i].bin_b64 = r.data.bin_b64 || "";
        list[i].xdf_b64 = r.data.xdf_b64 || "";
        last = null;
        break;
      } catch (e) { last = e; }
    }
    if (last) throw last;
    if (onProgress) onProgress(Math.min(99, Math.round(((i + 1) / list.length) * 99)));
  }
  let items = Array.isArray(local.items) ? local.items : [];
  let ecu = Array.isArray(local.ecu_records) ? local.ecu_records : [];
  let added = 0;
  if (part === "all" || part === "map") {
    if (data.incremental) {
      const byId = new Map(items.map((it) => [it.id, it]));
      for (const id of data.removed || []) byId.delete(id);
      for (const it of data.items || []) byId.set(it.id, it);
      items = [...byId.values()];
    } else {
      items = data.items || [];
    }
    items.sort((a, b) => String(a.part_code || "").localeCompare(String(b.part_code || "")));
    added = (data.items || []).length;
  }
  if (part === "all" || part === "ecu") {
    ecu = data.ecu_records || [];
    if (part === "ecu") added = ecu.length;
  }
  const merged = { version: data.version, updated_at: data.updated_at, notes: data.notes, count: data.count, items, ecu_records: ecu };
  const json = JSON.stringify(merged);
  if (IS_WIN_EXE && window.chrome?.webview) {
    const b64 = btoa(unescape(encodeURIComponent(json)));
    window.chrome.webview.postMessage("mapdb:save:" + b64);
  } else {
    localStorage.setItem("trmd_mapdb", json);
  }
  if (typeof window !== "undefined") { window.__TRMD_MAPDB = true; window.__TRMD_MAPDB_VER = merged.version; }
  _db = merged;
  if (onProgress) onProgress(100);
  window.dispatchEvent(new CustomEvent("trmd-basemap-updated", { detail: { version: merged.version, added } }));
  return { ...merged, added, incremental: !!data.incremental, part };
}

function b64bytes(b) {
  const s = atob(b || "");
  const u = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i);
  return u;
}

export async function localFileBytes(id, kind) {
  const d = await getLocalDb();
  const it = d && d.items.find((x) => x.id === id);
  const b = it && (kind === "bin" ? it.bin_b64 : it.xdf_b64);
  if (!b) throw new Error("offline file missing");
  return b64bytes(b);
}
